<template>
  <v-card style="overflow: hidden;">
    <v-toolbar v-if="supportsRefresh" flat dense color="secondary" dark>
      <v-toolbar-title class="body-2">{{ title }}</v-toolbar-title>
      <v-spacer />
      <v-btn-toggle
        v-if="dateRangeEditable"
        v-model="selectedDateRange"
        mandatory
        class="date-range-toggle"
        @change="onDateRangeChange"
      >
        <v-btn
          v-for="(item, idx) in dateRangeShortcuts"
          :key="idx"
          flat
          small
          :value="item.value"
          :title="item.label"
          class="text-capitalize"
        >
          {{ item.shortLabel }}
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>
    <div v-if="statisticalSummary.length" class="px-3 pt-2">
      <div
        v-for="(line, i) in statisticalSummary"
        :key="i"
        class="body-2 summary-line"
      >{{ line }}</div>
    </div>
    <v-progress-linear indeterminate color="blue" class="my-0" v-if="loading" />
    <v-alert outline class="py-1 ma-0" color="warning" icon="assessment" :value="true" v-if="error">
      <div class="primary--text">{{ errorMessage }}</div>
    </v-alert>
    <div v-else :id="graphDivId" />
  </v-card>
</template>

<script>
import Highcharts from 'highcharts'
import DataCharts from '@/components/mixins/DataCharts'
import axios from 'axios'
import moment from 'moment'
import { dateRangePresets, dateRangeShortcuts as dateRangeShortcutOptions } from './dateRange'

const statisticAliases = {
  sum: 'total',
  total: 'total',
  avg: 'average',
  mean: 'average',
  average: 'average',
  max: 'max',
  maximum: 'max',
  min: 'min',
  minimum: 'min',
}

function normalizeStatistics(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const result = []
  value.forEach(raw => {
    if (typeof raw !== 'string') return
    const name = statisticAliases[raw.trim().toLowerCase()]
    if (!name || seen.has(name)) return
    seen.add(name)
    result.push(name)
  })
  return result
}

export default {
  name: 'TimeSeriesGraphWidget',
  mixins: [DataCharts],
  props: {
    widgetId: { type: String, required: true },
    config: { type: Object, default: () => ({}) },
    globalDateRange: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: null,
      selectedDateRange: this.config.dateRange || this.globalDateRange || 'pastTwoWeeks',
      statisticalSummary: [],
    }
  },
  computed: {
    graphDivId() {
      return `tsg-${this.widgetId}`
    },
    title() {
      return this.config.title || 'Time Series Graph'
    },
    series() {
      return this.config.series || []
    },
    supportsRefresh() {
      return this.config.supportsRefresh !== false
    },
    dateRangeEditable() {
      return this.config.dateRangeEditable !== false
    },
    showLegend() {
      if (this.config.showLegend !== undefined) return this.config.showLegend
      return this.series.length > 1
    },
    dateRange() {
      const preset = this.selectedDateRange || 'pastTwoWeeks'
      const days = dateRangePresets[preset] || 14
      return {
        preset,
        start: moment().subtract(days, 'days'),
        end: moment(),
      }
    },
    dateRangeShortcuts() {
      return dateRangeShortcutOptions
    },
    statistics() {
      return normalizeStatistics(this.config.statistics)
    },
  },
  watch: {
    globalDateRange(newValue) {
      if (newValue == null) return
      this.selectedDateRange = newValue
      this.refresh()
    },
  },
  methods: {
    onDateRangeChange() {
      this.refresh()
    },
    async initialize(force = false) {
      if (!this.series.length) {
        this.error = true
        this.errorMessage = 'No time series configured.'
        return
      }

      const cachedData = this.getCachedGraphData()
      if (!force && cachedData) {
        this.drawGraph(cachedData)
        return
      }

      this.loading = true
      this.error = false
      this.errorMessage = null
      this.statisticalSummary = []
      try {
        const seriesData = await this.fetchGraphData()
        if (seriesData == null || seriesData.length === 0) {
          this.error = true
          this.errorMessage = 'No data available for the selected time range.'
          this.loading = false
          this.updateStatisticalSummary(null)
          return
        }
        this.$store.dispatch('dashboard/setWidgetData', { widgetId: this.widgetId, data: seriesData })
        this.drawGraph(seriesData)
      } catch (err) {
        this.error = true
        this.errorMessage = err.message || 'Failed to load graph data.'
        this.loading = false
        this.statisticalSummary = []
        this.$store.dispatch('dashboard/setWidgetError', { widgetId: this.widgetId, error: err })
      }
    },
    async refresh() {
      await this.initialize(true)
    },
    getCachedGraphData() {
      const state = this.$store.getters['dashboard/widgetState'](this.widgetId)
      return state && Array.isArray(state.data) ? state.data : null
    },
    statisticParameter() {
      const raw = this.config.title || ''
      const commaIndex = raw.indexOf(',')
      const subtitle = (commaIndex === -1 ? raw : raw.slice(commaIndex + 1)).trim()
      const words = subtitle.split(/\s+/).filter(Boolean)
      return words.length ? words[words.length - 1].toLowerCase() : ''
    },
    capitalizeFirst(value) {
      return value.charAt(0).toUpperCase() + value.slice(1)
    },
    formatStatValue(value) {
      return String(Number(value.toFixed(2)))
    },
    computeStatistic(name, values) {
      switch (name) {
        case 'total':
          return values.reduce((sum, value) => sum + value, 0)
        case 'max':
          return Math.max(...values)
        case 'min':
          return Math.min(...values)
        case 'average':
          return values.reduce((sum, value) => sum + value, 0) / values.length
        default:
          return null
      }
    },
    buildNoDataLine() {
      return 'No data available'
    },
    buildResultLine(name, unit, value) {
      const parameter = this.statisticParameter()
      const label = `${this.capitalizeFirst(name)}${parameter ? ' ' + parameter : ''}`
      const suffix = unit ? ` ${unit}` : ''
      return `${label}: ${value}${suffix}`
    },
    updateStatisticalSummary(seriesData) {
      const statistics = this.statistics
      if (!statistics.length) {
        this.statisticalSummary = []
        return
      }

      if (!seriesData || !seriesData.length) {
        this.statisticalSummary = [this.buildNoDataLine()]
        return
      }

      const lines = []
      let noDataAdded = false
      seriesData.forEach(series => {
        const values = (series.data || [])
          .map(point => point[1])
          .filter(value => value != null && isFinite(Number(value)))
          .map(Number)
        if (!values.length) {
          if (!noDataAdded) {
            lines.push(this.buildNoDataLine())
            noDataAdded = true
          }
          return
        }
        statistics.forEach(name => {
          const result = this.computeStatistic(name, values)
          if (result == null) return
          lines.push(this.buildResultLine(name, series.unit, this.formatStatValue(result)))
        })
      })
      this.statisticalSummary = lines
    },
    drawGraph(seriesData) {
      this.updateStatisticalSummary(seriesData)
      this.loading = false
      this.error = false
      this.errorMessage = null
      this.$nextTick(() => {
        const args = this.buildGraphArgs(seriesData)
        try {
          this.drawChartWithArgs(args, seriesData, { renderTo: this.graphDivId, tooltip: false })
        } catch (err) {
          console.warn('Failed to draw the chart', err)
        }
      })
    },
    async fetchGraphData() {
      const startDate = this.dateRange.start
      const endDate = this.dateRange.end
      const body = this.series.map(s => ({
        DataSetIdentifer: s.dataSetIdentifer,
        LocationName: s.locationName,
      }))
      const url =
        'api/GraphAPI?startDate=' +
        startDate.format('YYYY-MM-DD') +
        '&endDate=' +
        endDate.format('YYYY-MM-DD') +
        '&includeTime=false&tryCompression=true&includeGapMarkers=false'
      const res = await axios.post(url, body)
      const responseData = res.data
      if (!responseData || responseData.length === 0) {
        return null
      }
      const result = []
      for (let i = 0; i < responseData.length; i++) {
        const dataForSeries = responseData[i]
        const rows = dataForSeries.Rows.map(x => [moment(x.DateTime).valueOf(), x.Value])
        const approvalLevels = dataForSeries.Rows.map(x => x.ApprovalLevel)
        const dataGrades = dataForSeries.Rows.map(x => x.Grade)
        const dataQualifiers = dataForSeries.Rows.map(x => x.Qualifier)
        const matchedConfig = this.series[i] || this.series[0]
        result.push({
          name: dataForSeries.DataSetName,
          data: rows,
          dataQaQc: approvalLevels,
          dataGrades: dataGrades,
          dataQualifiers: dataQualifiers,
          yAxis: 0,
          type: matchedConfig.type || this.decideChartType(dataForSeries.DataSetName),
          unit: dataForSeries.Unit,
          color: matchedConfig.color || Highcharts.getOptions().colors[i % 10],
        })
      }
      return result
    },
    buildGraphArgs(seriesData) {
      const graphTitle = `${this.title}  (${this.dateRange.start.format('YYYY-MMM-DD')} to ${this.dateRange.end.format('YYYY-MMM-DD')})`
      const unit = this.resolveYAxisUnit(seriesData)
      const yAxisTitle = unit ? `${this.title} (${unit})` : this.title
      return {
        title: graphTitle,
        timeRange: [this.dateRange.start, this.dateRange.end],
        showLegend: this.showLegend,
        datasets: this.series.map((s, i) => ({
          name: s.dataSetIdentifer,
          type: s.type || this.decideChartType(s.dataSetIdentifer),
          yAxis: 0,
          color: s.color || Highcharts.getOptions().colors[i % 10],
        })),
        yAxis: [
          {
            labels: { format: '', style: { color: '#434348' } },
            title: { text: yAxisTitle, style: { color: '#434348' } },
          },
        ],
      }
    },
    resolveYAxisUnit(seriesData) {
      if (!seriesData || !seriesData.length) return null
      const units = [...new Set(seriesData.map(s => s.unit).filter(Boolean))]
      return units.length === 1 ? units[0] : null
    },
  },
}
</script>

<style scoped>
.date-range-toggle {
  margin-right: 8px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 4px;
}
.summary-line {
  color: #000;
  word-wrap: break-word;
}
</style>
